import React, { useEffect, useRef, useState } from 'react';
import PageHero from '../components/PageHero';
import GetInvolved from '../components/GetInvolved';
import './Impact.css';

/* ── Count-up hook ───────────────────────────────────────── */
function useCountUp(target, duration = 2000, active = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const step = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, active]);
  return count;
}

/* ── Data ────────────────────────────────────────────────── */
const TOP_NUMBERS = [
  { icon: '👨‍👩‍👧‍👦', value: 50000, suffix: '+', label: 'Lives Impacted', sub: 'Directly across all programmes', colorClass: 'inc-teal' },
  { icon: '🏫', value: 120, suffix: '+', label: 'Villages Served', sub: 'Across 5 Tamil Nadu districts', colorClass: 'inc-amber' },
  { icon: '🎓', value: 8500, suffix: '+', label: 'Children Educated', sub: 'Annual scholarship & school support', colorClass: 'inc-blue' },
  { icon: '💚', value: 15, suffix: ' Yrs', label: 'Years of Service', sub: 'Founded 2010, still growing', colorClass: 'inc-green' },
];

const PROGRAM_BARS = [
  { icon: '🎓', label: 'Education', count: '8,500+ students', pct: 78, color: '#0F766E' },
  { icon: '💊', label: 'Healthcare', count: '12,000+ check-ups', pct: 65, color: '#D97706' },
  { icon: '👩‍💼', label: 'Women Empowerment', count: '3,200+ women', pct: 55, color: '#2563EB' },
  { icon: '🌾', label: 'Sustainable Farming', count: '1,800+ farmers', pct: 70, color: '#059669' },
  { icon: '🧒', label: 'Child Welfare', count: '5,000+ children', pct: 80, color: '#E11D48' },
  { icon: '🏘️', label: 'Rural Development', count: '120 villages', pct: 42, color: '#7C3AED' },
];

const QUICK_STATS = [
  { icon: '🏥', num: '40+', lbl: 'Medical camps/year' },
  { icon: '💧', num: '12', lbl: 'Water projects built' },
  { icon: '🏆', num: '6', lbl: 'National awards' },
  { icon: '👥', num: '80+', lbl: 'Full-time staff' },
];

const GROWTH_YEARS = [
  { year: '2019', lives: 45, funds: 32 },
  { year: '2020', lives: 52, funds: 38 },
  { year: '2021', lives: 61, funds: 47 },
  { year: '2022', lives: 72, funds: 58 },
  { year: '2023', lives: 88, funds: 70 },
  { year: '2024', lives: 100, funds: 85 },
];

const DISTRICTS = [
  { icon: '📍', name: 'Villupuram',   sub: 'Education & Child Welfare', count: '18 villages' },
  { icon: '📍', name: 'Dindigul',     sub: 'Farming & Healthcare',     count: '22 villages' },
  { icon: '📍', name: 'Salem',        sub: 'Women Empowerment',        count: '15 villages' },
  { icon: '📍', name: 'Tirunelveli', sub: 'Rural Development',         count: '28 villages' },
  { icon: '📍', name: 'Erode',        sub: 'Water & Sanitation',       count: '37 villages' },
];

const PIN_POSITIONS = [
  { top: '35%', left: '42%' }, { top: '55%', left: '38%' },
  { top: '28%', left: '60%' }, { top: '62%', left: '55%' },
  { top: '48%', left: '28%' }, { top: '40%', left: '52%' },
  { top: '70%', left: '45%' }, { top: '22%', left: '48%' },
];

const STORIES = [
  {
    icon: '🎓', tag: 'Education',
    image: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=700&h=500&q=80',
    title: 'From a Leaking Classroom to a Digital Lab',
    text: 'In 2022 we renovated Govt. High School, Sirugur — new roof, desks, and a 20-computer lab. Pass rates jumped from 58% to 89% in two years.',
    metric: '89% pass rate achieved',
  },
  {
    icon: '💊', tag: 'Healthcare',
    image: 'https://images.unsplash.com/photo-1758691462123-8a17ae95d203?auto=format&fit=crop&w=700&h=500&q=80',
    title: '200 Cataract Surgeries in One Camp',
    text: 'Our partnership with a Chennai hospital brought free eye surgery to Dindigul. 200 patients — some blind for years — regained full or partial vision.',
    metric: '200 sight restorations',
  },
  {
    icon: '👩', tag: 'Women',
    image: 'https://images.unsplash.com/photo-1708417134243-6d71770d82ef?auto=format&fit=crop&w=700&h=500&q=80',
    title: 'Self-Help Group Creates 45 Entrepreneurs',
    text: "Meena's SHG in Tirunelveli started with ₹500 each. Three years later, 45 women run profitable businesses — tailoring, pickles, and handicrafts.",
    metric: '₹4.2L average annual income',
  },
  {
    icon: '🌾', tag: 'Farming',
    image: 'https://images.unsplash.com/photo-1762884110133-926e4195d3b9?auto=format&fit=crop&w=700&h=500&q=80',
    title: 'Rainwater Harvest Ends a 10-Year Drought',
    text: 'Twelve villages in Erode were water-scarce every summer. Our check-dams and recharge pits now store 8 crore litres, supporting crops year-round.',
    metric: '8 crore litres stored',
  },
  {
    icon: '🧒', tag: 'Child Welfare',
    image: 'https://images.unsplash.com/photo-1698795635937-31264cdbf359?auto=format&fit=crop&w=700&h=500&q=80',
    title: 'Hot Meals, Better Grades',
    text: 'Children in 30 nutrition centres receive two hot meals daily. Absenteeism fell 60% and teachers report measurable improvement in concentration.',
    metric: '60% drop in absenteeism',
  },
  {
    icon: '🏘️', tag: 'Rural Dev.',
    image: 'https://images.unsplash.com/photo-1685023620523-9c726f2c499b?auto=format&fit=crop&w=700&h=500&q=80',
    title: 'Solar Lights Transform a Night Market',
    text: 'Eight tribal hamlets had no street lighting. After solar installation, women report feeling safe to walk at night and evening markets have doubled in footfall.',
    metric: '8 hamlets illuminated',
  },
];

const REPORTS = [
  { year: '2024', label: 'Annual Impact Report' },
  { year: '2023', label: 'Annual Impact Report' },
  { year: '2022', label: 'Annual Impact Report' },
  { year: '2021', label: 'Annual Impact Report' },
];

/* ── CountUp card ────────────────────────────────────────── */
function NumCard({ item, active }) {
  const raw = useCountUp(item.value, 2000, active);
  const fmt = n => n >= 1000 ? `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K` : `${n}`;
  return (
    <div className={`impact-num-card ${item.colorClass}`}>
      <span className="impact-num-icon" aria-hidden="true">{item.icon}</span>
      <div className="impact-num-value" aria-label={`${item.value}${item.suffix} ${item.label}`}>
        {fmt(raw)}{item.suffix}
      </div>
      <div className="impact-num-label">{item.label}</div>
      <div className="impact-num-sub">{item.sub}</div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */
export default function Impact() {
  const [numbersActive, setNumbersActive] = useState(false);
  const [barsActive, setBarsActive] = useState(false);
  const [growthActive, setGrowthActive] = useState(false);
  const numbersRef = useRef(null);
  const barsRef = useRef(null);
  const growthRef = useRef(null);

  useEffect(() => {
    const observe = (ref, setter) => {
      const obs = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) setter(true); },
        { threshold: 0.2 }
      );
      if (ref.current) obs.observe(ref.current);
      return obs;
    };
    const o1 = observe(numbersRef, setNumbersActive);
    const o2 = observe(barsRef, setBarsActive);
    const o3 = observe(growthRef, setGrowthActive);
    return () => { o1.disconnect(); o2.disconnect(); o3.disconnect(); };
  }, []);

  const maxLives = Math.max(...GROWTH_YEARS.map(y => y.lives));
  const maxFunds = Math.max(...GROWTH_YEARS.map(y => y.funds));

  return (
    <main id="main-content">
      <PageHero
        label="Our Impact"
        title="15 Years of Measurable Change"
        desc="Every programme is tracked, every outcome published. Here is what your support has achieved — in numbers, stories, and lives transformed."
        breadcrumb="Our Impact"
        bgImage="https://images.unsplash.com/photo-1550534791-2677533605ab?auto=format&fit=crop&w=1600&h=600&q=80"
      />

      {/* Top numbers */}
      <section className="impact-numbers section" ref={numbersRef} aria-label="Key impact numbers">
        <div className="container">
          <div className="section-header">
            <span className="section-label">By the Numbers</span>
            <h2 className="section-title">The Scale of Our Reach</h2>
            <p className="section-subtitle">Measured impact from 2010 to 2024 — independently audited and publicly reported.</p>
          </div>
          <div className="impact-numbers-grid" role="list">
            {TOP_NUMBERS.map(item => (
              <div role="listitem" key={item.label}>
                <NumCard item={item} active={numbersActive} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programme breakdown */}
      <section className="impact-breakdown section" ref={barsRef} aria-labelledby="breakdown-heading">
        <div className="container">
          <div className="breakdown-inner">
            <div>
              <span className="section-label">Programme Reach</span>
              <h2 className="section-title" id="breakdown-heading" style={{ textAlign: 'left' }}>
                Impact Across All Six Programmes
              </h2>
              <p className="section-subtitle" style={{ textAlign: 'left' }}>
                Progress against our 2024 annual targets — updated quarterly.
              </p>
              <div className="breakdown-cards" role="list" aria-label="Quick statistics">
                {QUICK_STATS.map(s => (
                  <div className="breakdown-card" key={s.lbl} role="listitem">
                    <div className="breakdown-card-icon" aria-hidden="true">{s.icon}</div>
                    <div className="breakdown-card-num">{s.num}</div>
                    <div className="breakdown-card-lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="breakdown-chart" aria-label="Programme reach chart">
              {PROGRAM_BARS.map(p => (
                <div className="breakdown-bar-row" key={p.label}>
                  <div className="breakdown-bar-meta">
                    <span className="breakdown-bar-label">
                      <span className="breakdown-bar-icon" aria-hidden="true">{p.icon}</span>
                      {p.label}
                    </span>
                    <div className="breakdown-bar-stats">
                      <span className="breakdown-bar-count">{p.count}</span>
                      <span className="breakdown-bar-pct">{p.pct}%</span>
                    </div>
                  </div>
                  <div
                    className="bar-track"
                    role="progressbar"
                    aria-valuenow={p.pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${p.label}: ${p.pct}% of annual target`}
                  >
                    <div
                      className="bar-fill"
                      style={{ width: barsActive ? `${p.pct}%` : '0%', background: p.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Year-over-year growth */}
      <section className="impact-growth section" ref={growthRef} aria-labelledby="growth-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Year-on-Year Growth</span>
            <h2 className="section-title" id="growth-heading">Consistent Growth Since 2019</h2>
            <p className="section-subtitle">Beneficiaries reached and funds utilised (indexed to 2024 = 100).</p>
          </div>

          <div className="growth-chart-wrap">
            <div className="growth-chart-title">
              Programme Growth Index
              <div className="growth-legend" aria-label="Chart legend">
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#0F766E' }} aria-hidden="true" />
                  Beneficiaries
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#F59E0B' }} aria-hidden="true" />
                  Funds utilised
                </div>
              </div>
            </div>

            <div className="growth-bars" role="list" aria-label="Year-over-year growth chart">
              {GROWTH_YEARS.map(y => (
                <div className="growth-year-group" key={y.year} role="listitem" aria-label={`${y.year}: beneficiaries ${y.lives}, funds ${y.funds}`}>
                  <div className="growth-bars-pair">
                    <div
                      className="growth-bar"
                      style={{
                        height: growthActive ? `${(y.lives / maxLives) * 160}px` : '0px',
                        background: 'var(--primary)',
                      }}
                      data-label={`${y.lives}`}
                      aria-hidden="true"
                    />
                    <div
                      className="growth-bar"
                      style={{
                        height: growthActive ? `${(y.funds / maxFunds) * 160}px` : '0px',
                        background: 'var(--secondary)',
                      }}
                      data-label={`${y.funds}`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="growth-year-label">{y.year}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Geographic reach */}
      <section className="impact-reach section" aria-labelledby="reach-heading">
        <div className="container">
          <div className="reach-inner">
            <div>
              <span className="section-label">Where We Work</span>
              <h2 className="section-title" id="reach-heading" style={{ textAlign: 'left' }}>
                Serving 120+ Villages Across 5 Districts
              </h2>
              <p className="section-subtitle" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                Our field teams live and work in the communities they serve — not just visiting, but embedded.
              </p>
              <div className="reach-districts" role="list" aria-label="Districts we serve">
                {DISTRICTS.map(d => (
                  <div className="reach-district-item" key={d.name} role="listitem">
                    <div className="reach-district-left">
                      <div className="reach-district-icon" aria-hidden="true">{d.icon}</div>
                      <div>
                        <div className="reach-district-name">{d.name}</div>
                        <div className="reach-district-sub">{d.sub}</div>
                      </div>
                    </div>
                    <div className="reach-district-count">{d.count}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="reach-map" role="img" aria-label="Map showing villages served across Tamil Nadu districts">
              <div className="reach-pins" aria-hidden="true">
                {PIN_POSITIONS.map((pos, i) => (
                  <div
                    key={i}
                    className="reach-pin"
                    style={{ top: pos.top, left: pos.left, animationDelay: `${i * 0.3}s` }}
                  />
                ))}
              </div>
              <div className="reach-map-icon" aria-hidden="true">🗺️</div>
              <div className="reach-map-text">Tamil Nadu — 5 Districts, 120+ Villages</div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact stories */}
      <section className="impact-stories section" aria-labelledby="stories-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">On the Ground</span>
            <h2 className="section-title" id="stories-heading">Stories of Transformation</h2>
            <p className="section-subtitle">Numbers tell one story. People tell another. Here are six moments that remind us why this work matters.</p>
          </div>
          <div className="stories-grid" role="list">
            {STORIES.map(s => (
              <article className="story-card" key={s.title} role="listitem">
                <div className="story-card-img">
                  <img src={s.image} alt={s.title} className="story-card-photo" loading="lazy" />
                  <div className="story-card-img-overlay" aria-hidden="true" />
                </div>
                <div className="story-card-body">
                  <span className="story-tag">{s.tag}</span>
                  <h3 className="story-card-title">{s.title}</h3>
                  <p className="story-card-text">{s.text}</p>
                  <div className="story-metric">
                    <span aria-hidden="true">✦</span> {s.metric}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Annual reports */}
      <section className="impact-reports section" aria-labelledby="reports-heading">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Transparency</span>
            <h2 className="section-title" id="reports-heading">Annual Impact Reports</h2>
            <p className="section-subtitle">Independently audited, publicly available. Every rupee accounted for.</p>
          </div>
          <div className="reports-grid" role="list">
            {REPORTS.map(r => (
              <button className="report-card" key={r.year} role="listitem" aria-label={`Download ${r.year} ${r.label}`}>
                <div className="report-icon" aria-hidden="true">📄</div>
                <div className="report-year">{r.year}</div>
                <div className="report-label">{r.label}</div>
                <div className="report-download">
                  <span aria-hidden="true">↓</span> Download PDF
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <GetInvolved />
    </main>
  );
}
